import { ApolloCache } from '@apollo/client';
import { CalendarDaysIcon, PauseCircleIcon, PlayCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { ChangeEventHandler, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionList, ActionListItem } from '../../../components/ActionList';
import { Button, CircleSpinner } from '../../../components/Button';
import { DayInput } from '../../../components/formik/FormDayInput';
import { Input } from '../../../components/inputs/Input';
import { ConfirmDialog, Modal } from '../../../components/Modal';
import { useSubscriptionTypeTranslations } from '../../../hooks/translations/useSubscriptionTypeTranslations';
import { useFormatDate } from '../../../hooks/useFormatDate';
import useOpen from '../../../hooks/useOpen';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { T, useT } from '../../../translation/src';
import {
  OrganisationReferralOverviewDocument,
  OrganisationReferralOverviewQuery,
  PlanChangeKind,
  PlanChargeLine,
  ReferralDealKind,
  ReferralOverviewFieldsFragment,
  ReferralRole,
  ReferralSaleFieldsFragment,
  SubscriptionType,
  useOrganisationBillingQuery,
  useOrganisationLinkBillingMutation,
  usePartnerCustomerBillingDatePreviewQuery,
  usePartnerCustomerCancelSubscriptionMutation,
  usePartnerCustomerChangePlanMutation,
  usePartnerCustomerPauseSubscriptionMutation,
  usePartnerCustomerPlanChangePreviewQuery,
  usePartnerCustomerPurchaseCreditsMutation,
  usePartnerCustomerSetBillingDateMutation,
} from '../../../types/graphqlTypes';
import { dayAfterTermEnd, toDateInput, tomorrowUtc } from '../../../utils/dates';
import { formatPrice } from '../../../utils/formatPrice';
import { graphqlErrorCode } from '../../../utils/graphqlErrors';
import { BillingInfoCard, BillingInfoModal } from '../BillingInfoCard';
import { ContactSalesModal } from '../ContactSalesModal';
import { cutPercent } from '../referrals/partnerRoles';
import { SalesHistory } from '../referrals/SalesHistory';

type Customer = ReferralOverviewFieldsFragment['referredOrganisations'][number];

// The plans a partner orders for a customer: the standard ones (a custom deal is sales' to make).
const ORDERABLE_PLANS = [SubscriptionType.Launch, SubscriptionType.Grow, SubscriptionType.Scale];
// The bounds of one project purchase (the server's MIN/MAX_PURCHASE_AMOUNT).
const MIN_PROJECTS = 3;
const MAX_PROJECTS = 1000;

const Section: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 p-5 dark:border-gray-700">
    <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</span>
    {children}
  </div>
);

// Every order answers with the partner's overview: written over the page's query (ReferralOverview has no id).
const useWriteOverview = (organisationId: string) =>
  useCallback(
    (overview: ReferralOverviewFieldsFragment | undefined, cache: ApolloCache) => {
      if (overview) cache.writeQuery<OrganisationReferralOverviewQuery>({ query: OrganisationReferralOverviewDocument, variables: { organisationId }, data: { organisationReferralOverview: overview } });
    },
    [organisationId],
  );

// A plan order as a receipt: the new plan for the months it runs, less what the term already paid, makes the total;
// the partner's discount off it makes its price, what its invoice for the order says.
const PlanPriceBreakdown: React.FC<{ lines: Pick<PlanChargeLine, 'subscriptionType' | 'from' | 'months' | 'totalCents'>[]; paidCents: number; amountCents: number; cutBasisPoints: number; first: boolean }> = ({
  lines,
  paidCents,
  amountCents,
  cutBasisPoints,
  first,
}) => {
  const plans = useSubscriptionTypeTranslations();
  const cutCents = Math.round((amountCents * Math.max(0, cutBasisPoints)) / 10000);
  const row = 'flex justify-between gap-4';
  return (
    <div className="flex max-w-md flex-col gap-0.5">
      {lines.map((line) => (
        <div key={`${line.subscriptionType}-${line.from}`} className={classNames(row, 'text-gray-500')}>
          <T _str="{plan} · {months} month(s)" args={{ plan: plans[line.subscriptionType], months: line.months }} />
          <span>{formatPrice(line.totalCents)}</span>
        </div>
      ))}
      {paidCents > 0 && (
        <div className={classNames(row, 'text-gray-500')}>
          <T _str="already paid for this term" swc />
          <span>− {formatPrice(paidCents)}</span>
        </div>
      )}
      <div className={classNames(row, 'mt-1 border-t border-gray-300 pt-1 dark:border-gray-600')}>
        <T _str="total" swc />
        <span>{formatPrice(amountCents)}</span>
      </div>
      {cutCents > 0 && (
        <div className={classNames(row, 'text-gray-500')}>
          <span>
            {first ? <T _str="first-order discount" swc /> : <T _str="discount" swc />} ({cutPercent(cutBasisPoints)}%)
          </span>
          <span>− {formatPrice(cutCents)}</span>
        </div>
      )}
      <div className={classNames(row, 'font-semibold')}>
        <T _str="your price (before VAT)" swc />
        <span>{formatPrice(amountCents - cutCents)}</span>
      </div>
    </div>
  );
};

// What the partner pays for an order, as one sum: the list price (n × the unit price, for projects), minus its
// discount, equals its price, what its invoice for the order says.
const InvoicedPrice: React.FC<{ amountCents: number; cutBasisPoints: number; first: boolean; listPrice?: string }> = ({ amountCents, cutBasisPoints, first, listPrice }) => {
  const cutCents = Math.round((amountCents * Math.max(0, cutBasisPoints)) / 10000);
  return (
    <span className="text-sm">
      {listPrice ?? formatPrice(amountCents)}
      {cutCents > 0 && (
        <span className="text-gray-500">
          {' − '}
          {cutPercent(cutBasisPoints)}% {first ? <T _str="first-order discount" /> : <T _str="discount" />}
        </span>
      )}
      {' = '}
      <strong>{formatPrice(amountCents - cutCents)}</strong>{' '}
      <span className="text-gray-500">
        <T _str="your price (before VAT)" />
      </span>
    </span>
  );
};

export interface CustomerSubscriptionModalProps {
  open?: boolean;
  onClose: () => void;
  // The partner.
  organisationId: string;
  customer: Customer;
  sales: ReferralSaleFieldsFragment[];
  // The partner's discounts, in basis points: on the customer's opening subscription, and on everything after it.
  cuts: { first: number; recurring: number };
  // The partner's role (a reseller or a full-service partner), for the words of its orders.
  role: ReferralRole;
}
// What a reseller or full-service partner orders for a customer billed to it: its plan, extra projects, the end
// of its subscription, a pause, its billing date. Priced as the customer would be; the partner is invoiced and
// sees what it owes, its discount already off. Below, what was ordered for this customer.
const CustomerSubscriptionModal_: React.FC<CustomerSubscriptionModalProps> = ({ open, onClose, organisationId, customer, sales, cuts, role }) => {
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const { showToast } = useToastNotifications();
  const failed = useT('the order did not go through', { swc: true });
  const ordered = useT('ordered', { swc: true });
  const writeOverview = useWriteOverview(organisationId);
  const customerOrganisationId = customer.id;
  const base = useMemo(() => ({ organisationId, customerOrganisationId }), [customerOrganisationId, organisationId]);

  const [changePlan, { loading: changingPlan }] = usePartnerCustomerChangePlanMutation({ update: (cache, { data }) => writeOverview(data?.partnerCustomerChangePlan, cache) });
  const [purchase, { loading: purchasing }] = usePartnerCustomerPurchaseCreditsMutation({ update: (cache, { data }) => writeOverview(data?.partnerCustomerPurchaseCredits, cache) });
  const [cancel] = usePartnerCustomerCancelSubscriptionMutation({ update: (cache, { data }) => writeOverview(data?.partnerCustomerCancelSubscription, cache) });
  const [pause] = usePartnerCustomerPauseSubscriptionMutation({ update: (cache, { data }) => writeOverview(data?.partnerCustomerPauseSubscription, cache) });
  const [setBillingDate, { loading: settingBillingDate }] = usePartnerCustomerSetBillingDateMutation({ update: (cache, { data }) => writeOverview(data?.partnerCustomerSetBillingDate, cache) });

  // The partner is invoiced: no order without its billing details. The card asks for them and the order buttons
  // wait; without them the server prices every sale at 0, so no price is shown either. Only a definite
  // "incomplete" blocks (Odoo off or unreachable does not).
  const { data: billingData, error: billingError, refetch: refetchBilling } = useOrganisationBillingQuery({ variables: { organisationId }, skip: !open, fetchPolicy: 'cache-and-network' });
  const billing = billingError ? undefined : billingData?.organisationBilling;
  const billingMissing = !!billing?.enabled && !billing.complete;
  const { open: billingModalOpen, onOpen: openBillingModal, onClose: closeBillingModal } = useOpen();
  // A partner without a customer in Odoo is linked to it first, once per opening (see BuyCreditsModal). Nothing
  // to link: the way to sales instead of the card.
  const [linkBilling, { loading: linking, called: linkTried, reset: resetLink }] = useOrganisationLinkBillingMutation({ refetchQueries: ['partnerCustomerPlanChangePreview', 'partnerCustomerBillingDatePreview'] });
  const unlinked = !!billing?.enabled && !billing.linked;
  useEffect(() => {
    if (!open) {
      if (!linkTried) return;
      const timeout = setTimeout(resetLink, 300);
      return () => clearTimeout(timeout);
    }
    if (unlinked && !linkTried) linkBilling({ variables: { organisationId } }).catch(() => undefined);
  }, [linkBilling, linkTried, open, organisationId, resetLink, unlinked]);
  const linkPending = unlinked && (!linkTried || linking);
  const linkFailed = unlinked && linkTried && !linking;
  const { open: contactSalesOpen, onOpen: openContactSales, onClose: closeContactSales } = useOpen();

  const run = useCallback(
    async (order: () => Promise<unknown>) => {
      try {
        await order();
        showToast({ title: ordered, text: customer.name, type: ToastType.SUCCESS });
        return true;
      } catch (e) {
        // The details went missing since the modal opened (edited in Odoo): the card asks for them again.
        if (graphqlErrorCode(e) === 'BILLING_INFO_MISSING') await refetchBilling();
        else showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
        return false;
      }
    },
    [customer.name, failed, ordered, refetchBilling, showToast],
  );

  // The plan: the current one selected, another picked to see what it means and costs, confirmed. The current one
  // is only orderable to call off the change waiting at the end of the term (a downgrade, the end), and only once
  // picked: until then the section says what is waiting rather than offering to call it off.
  const current = ORDERABLE_PLANS.includes(customer.subscriptionType) ? customer.subscriptionType : null;
  const [picked, setPicked] = useState<SubscriptionType | null>(null);
  const plan = picked ?? current;
  const orderable = !!picked && (picked !== current || !!customer.scheduledType);
  // A cheaper plan waiting for the end of the term: the day it starts.
  const downgradeTo = customer.scheduledType && customer.scheduledType !== SubscriptionType.Flex && customer.scheduledAt ? customer.scheduledType : null;
  const downgradeOn = downgradeTo && customer.scheduledAt ? formatDay(dayAfterTermEnd(customer.scheduledAt)) : '';
  const { data: planData, loading: planLoading } = usePartnerCustomerPlanChangePreviewQuery({ variables: { ...base, subscriptionType: plan ?? SubscriptionType.Grow }, skip: !open || !orderable, fetchPolicy: 'network-only' });
  const planPreview = orderable ? planData?.partnerCustomerPlanChangePreview : undefined;
  const onOrderPlan = useCallback(async () => {
    if (plan && (await run(() => changePlan({ variables: { ...base, subscriptionType: plan } })))) setPicked(null);
  }, [base, changePlan, plan, run]);

  // Extra projects.
  const [projects, setProjects] = useState(String(MIN_PROJECTS));
  const projectCount = Number(projects);
  const projectsValid = Number.isInteger(projectCount) && projectCount >= MIN_PROJECTS && projectCount <= MAX_PROJECTS;
  const onChangeProjects: ChangeEventHandler<HTMLInputElement> = useCallback((event) => setProjects(event.target.value), []);
  const onBuy = useCallback(() => run(() => purchase({ variables: { ...base, amount: projectCount } })), [base, projectCount, purchase, run]);

  // The billing date.
  const [billingDateOpen, setBillingDateOpen] = useState(false);
  const toggleBillingDate = useCallback(() => setBillingDateOpen((open) => !open), []);
  const billingDateLabel = useT('billing date', { swc: true });
  const [renewalDate, setRenewalDate] = useState(customer.subscriptionEndsAt ? toDateInput(dayAfterTermEnd(customer.subscriptionEndsAt)) : '');
  const { data: dateData } = usePartnerCustomerBillingDatePreviewQuery({ variables: { ...base, renewalDate }, skip: !open || !billingDateOpen || !renewalDate || !customer.subscriptionEndsAt, fetchPolicy: 'network-only' });
  const datePreview = dateData?.partnerCustomerBillingDatePreview;
  const onSetBillingDate = useCallback(async () => {
    if (await run(() => setBillingDate({ variables: { ...base, renewalDate } }))) setBillingDateOpen(false);
  }, [base, renewalDate, run, setBillingDate]);

  const { open: cancelOpen, onOpen: openCancel, onClose: closeCancel } = useOpen();
  const { open: pauseOpen, onOpen: openPause, onClose: closePause } = useOpen();
  const onPause = useCallback(async () => {
    if (await run(() => pause({ variables: { ...base, paused: !customer.subscriptionPaused } }))) closePause();
  }, [base, closePause, customer.subscriptionPaused, pause, run]);
  const onCancel = useCallback(async () => {
    if (await run(() => cancel({ variables: base }))) closeCancel();
  }, [base, cancel, closeCancel, run]);

  const running = customer.subscriptionType !== SubscriptionType.Flex;
  const ending = customer.scheduledType === SubscriptionType.Flex;
  const customerSales = useMemo(() => sales.filter((sale) => sale.referredOrganisationId === customer.id), [customer.id, sales]);
  // The server's rule (attribution): a new subscription is the first deal, once per customer.
  const planIsFirstDeal = planPreview?.kind === PlanChangeKind.NewSubscription && !customerSales.some((sale) => sale.dealKind === ReferralDealKind.First);

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="flex flex-col w-full max-w-4xl h-[85vh] max-h-[60rem]">
      <Modal.Header onClose={onClose} className="shrink-0">
        {customer.name}
      </Modal.Header>
      <Modal.Body className="flex min-h-0 flex-col gap-4 overflow-y-auto pr-2 grow">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-full bg-neon-green-300 px-3 py-0.5 font-semibold text-eerie-black">{plans[customer.subscriptionType]}</span>
          {/* A pending downgrade has its own card below. */}
          {running && customer.subscriptionEndsAt && !downgradeTo && (
            <span className="text-gray-500">
              {ending ? (
                <T _str="ends on {date}" args={{ date: formatDay(customer.subscriptionEndsAt) }} />
              ) : (
                <T _str="renews automatically on {date}" args={{ date: formatDay(dayAfterTermEnd(customer.subscriptionEndsAt)) }} />
              )}
            </span>
          )}
          {customer.subscriptionPaused && (
            <span className="rounded-full bg-amber-500/20 px-3 py-0.5 text-amber-700 dark:text-amber-400">
              <T _str="paused" swc />
            </span>
          )}
        </div>
        {downgradeTo && current && (
          <div className="flex flex-col gap-0.5 rounded-md border border-warning-yellow-500 bg-warning-yellow-500/10 p-3 text-sm">
            <span className="font-semibold">
              <T _str="Downgrades to {plan} on {date}." args={{ plan: plans[downgradeTo], date: downgradeOn }} />
            </span>
            <span className="text-gray-500">
              {/* Each placeholder once: the translation fills in only a placeholder's first occurrence. */}
              <T _str="{current} runs until then. To keep it, pick it under plan." args={{ current: plans[current] }} />
            </span>
          </div>
        )}

        {linkPending ? (
          <div className="flex justify-center py-2">
            <CircleSpinner className="h-6 w-6" />
          </div>
        ) : linkFailed ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed border-gray-300 p-3 text-sm dark:border-gray-600">
            <span className="flex flex-col">
              <span className="font-semibold">
                <T _str="Your organisation is not set up for ordering yet." />
              </span>
              <span className="text-gray-500">
                <T _str="Contact sales and we will get you started, so you can order for your customers here." />
              </span>
            </span>
            <Button variant="primary" size="sm" onClick={openContactSales}>
              <T _str="contact sales" swc />
            </Button>
          </div>
        ) : (
          billingMissing && <BillingInfoCard billing={billing} onEdit={openBillingModal} />
        )}

        <Section title={<T _str="plan" swc />}>
          <div className="flex flex-wrap gap-2">
            {ORDERABLE_PLANS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setPicked(option === current && !customer.scheduledType ? null : option)}
                className={classNames(
                  'rounded-full border px-4 py-1.5 text-sm font-semibold transition',
                  plan === option ? 'border-black bg-neon-green-300 text-eerie-black dark:border-white' : 'border-gray-300 hover:border-black dark:border-gray-600 dark:hover:border-white',
                )}
              >
                {plans[option]}
                {customer.subscriptionType === option && (
                  <span className="ml-1 text-xs font-normal">
                    (<T _str="current" />)
                  </span>
                )}
                {downgradeTo === option && (
                  <span className="ml-1 text-xs font-normal">
                    (<T _str="from {date}" args={{ date: downgradeOn }} />)
                  </span>
                )}
              </button>
            ))}
          </div>
          {plan &&
            orderable &&
            (planLoading && !planPreview ? (
              <span className="text-sm text-gray-500">…</span>
            ) : (
              planPreview && (
                <div className="flex flex-col gap-2 text-sm">
                  {planPreview.problems.length > 0 ? (
                    <span className="text-error-red-500">{planPreview.problems.join(', ')}</span>
                  ) : planPreview.kind === PlanChangeKind.ScheduledDowngrade ? (
                    <T _str="{plan} starts when the running term ends, on {date}. Nothing is invoiced before then." args={{ plan: plans[plan], date: formatDay(dayAfterTermEnd(planPreview.effectiveAt)) }} />
                  ) : planPreview.kind === PlanChangeKind.Unchanged ? (
                    downgradeTo ? (
                      <T _str="{plan} keeps running after {date}. The downgrade to {next} is called off." args={{ plan: plans[plan], date: downgradeOn, next: plans[downgradeTo] }} />
                    ) : (
                      <T _str="The subscription runs on as it is: the change waiting at the end of the term is called off." />
                    )
                  ) : (
                    !billingMissing && (
                      <PlanPriceBreakdown lines={planPreview.lines} paidCents={planPreview.currentTermValueCents} amountCents={planPreview.chargedCents} cutBasisPoints={planIsFirstDeal ? cuts.first : cuts.recurring} first={planIsFirstDeal} />
                    )
                  )}
                  <div>
                    <Button variant="primary" size="sm" onClick={onOrderPlan} loading={changingPlan} disabled={planPreview.problems.length > 0 || billingMissing}>
                      {planPreview.kind === PlanChangeKind.Upgrade ? (
                        <T _str="upgrade" swc />
                      ) : planPreview.kind === PlanChangeKind.ScheduledDowngrade ? (
                        <T _str="downgrade" swc />
                      ) : planPreview.kind === PlanChangeKind.Unchanged ? (
                        <T _str="keep {plan}" args={{ plan: plans[plan] }} swc />
                      ) : (
                        <T _str="order" swc />
                      )}
                    </Button>
                  </div>
                </div>
              )
            ))}
        </Section>

        <Section title={<T _str="extra projects" swc />}>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Input type="number" min={MIN_PROJECTS} max={MAX_PROJECTS} value={projects} onChange={onChangeProjects} className="w-28" />
            <Button variant="primary" size="sm" onClick={onBuy} loading={purchasing} disabled={!projectsValid || billingMissing}>
              <T _str="buy" swc />
            </Button>
          </div>
          {projectsValid && <InvoicedPrice amountCents={projectCount * customer.projectUnitPriceCents} cutBasisPoints={cuts.recurring} first={false} listPrice={`${projectCount} × ${formatPrice(customer.projectUnitPriceCents)}`} />}
        </Section>

        {running && (
          <Section title={<T _str="subscription" swc />}>
            {/* The rarer changes, as one list: each says what it does; the billing date opens right here. */}
            <ActionList>
              <ActionListItem
                icon={CalendarDaysIcon}
                title={<T _str="change billing date" swc />}
                description={
                  customer.subscriptionEndsAt ? (
                    <T _str="Renews on {date}. Move the day it renews and is invoiced" args={{ date: formatDay(dayAfterTermEnd(customer.subscriptionEndsAt)) }} />
                  ) : (
                    <T _str="Move the day it renews and is invoiced" />
                  )
                }
                onClick={toggleBillingDate}
                disabledReason={customer.subscriptionPaused ? <T _str="Resume the subscription first." /> : undefined}
              />
              {billingDateOpen && (
                <div className="flex flex-col gap-3 bg-gray-50 px-4 py-3 dark:bg-white/5">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <DayInput min={tomorrowUtc()} value={renewalDate} onChange={setRenewalDate} className="w-44" aria-label={billingDateLabel} />
                    {datePreview &&
                      (datePreview.problems.length ? (
                        <span className="text-error-red-500">{datePreview.problems.join(', ')}</span>
                      ) : billingMissing ? null : datePreview.chargedCents > 0 ? (
                        <InvoicedPrice amountCents={datePreview.chargedCents} cutBasisPoints={cuts.recurring} first={false} />
                      ) : (
                        <span className="text-gray-500">
                          <T _str="Nothing is invoiced." />
                        </span>
                      ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm" onClick={onSetBillingDate} loading={settingBillingDate} disabled={!datePreview || datePreview.problems.length > 0 || billingMissing}>
                      <T _str="apply" swc />
                    </Button>
                    <Button variant="secondary" size="sm" onClick={toggleBillingDate}>
                      <T _str="cancel" swc />
                    </Button>
                  </div>
                </div>
              )}
              <ActionListItem
                icon={customer.subscriptionPaused ? PlayCircleIcon : PauseCircleIcon}
                title={customer.subscriptionPaused ? <T _str="resume subscription" swc /> : <T _str="pause subscription" swc />}
                description={customer.subscriptionPaused ? <T _str="Uploads and orders work again" /> : <T _str="Temporarily suspend the subscription. The renewal date stays the same." />}
                onClick={openPause}
                danger={!customer.subscriptionPaused}
              />
              {!ending && (
                <ActionListItem
                  icon={XCircleIcon}
                  title={<T _str="cancel subscription" swc />}
                  description={
                    customer.subscriptionEndsAt ? (
                      <T _str="Remains active until {date} and will not renew." args={{ date: formatDay(customer.subscriptionEndsAt) }} />
                    ) : (
                      <T _str="Remains active until the end of the term and will not renew." />
                    )
                  }
                  onClick={openCancel}
                  danger
                />
              )}
            </ActionList>
          </Section>
        )}

        <SalesHistory sales={customerSales} role={role} hideCustomer />
      </Modal.Body>
      <ConfirmDialog
        open={pauseOpen}
        onClose={closePause}
        onConfirm={onPause}
        variant={customer.subscriptionPaused ? 'info' : 'warning'}
        title={customer.subscriptionPaused ? <T _str="resume the subscription of {name}?" args={{ name: customer.name }} swc /> : <T _str="pause the subscription of {name}?" args={{ name: customer.name }} swc />}
        message={
          customer.subscriptionPaused ? (
            <T _str="Uploads and orders work again. The term's dates stay as they are." />
          ) : (
            <T _str="Uploads and orders are blocked until you resume it. The term runs on: monthly projects, renewal and invoices go on as usual." />
          )
        }
        confirmLabel={customer.subscriptionPaused ? <T _str="resume" swc /> : <T _str="pause" swc />}
      />
      <ConfirmDialog
        open={cancelOpen}
        onClose={closeCancel}
        onConfirm={onCancel}
        title={<T _str="cancel the subscription of {name}?" args={{ name: customer.name }} swc />}
        message={<T _str="It runs to the end of the term, then stops instead of renewing. Nothing is refunded. Ordering the same plan again before then calls it off." />}
        confirmLabel={<T _str="cancel at term end" swc />}
      />
      {billing?.enabled && <BillingInfoModal open={billingModalOpen} onClose={closeBillingModal} organisationId={organisationId} info={billing.info} />}
      <ContactSalesModal open={contactSalesOpen} onClose={closeContactSales} organisationId={organisationId} />
    </Modal.Container>
  );
};
export const CustomerSubscriptionModal = memo(CustomerSubscriptionModal_);
